'use client';

import { useState, useEffect } from 'react';

interface TodoFormProps {
  onSubmit: (data: {
    title: string;
    details?: string;
    due_date?: string;
    delegate?: string;
    sensitive?: boolean;
  }) => void;
  onCancel?: () => void;
  compact?: boolean;
  initialData?: {
    title: string;
    details?: string | null;
    due_date?: string | null;
    delegate?: string | null;
    sensitive?: boolean;
  };
  decisionId?: string | null;
  readOnly?: boolean;
}

export default function TodoForm({
  onSubmit,
  onCancel,
  compact = false,
  initialData,
  decisionId,
  readOnly = false,
}: TodoFormProps) {
  const [title, setTitle] = useState(initialData?.title || '');
  const [details, setDetails] = useState(initialData?.details || '');
  const [dueDate, setDueDate] = useState(initialData?.due_date || '');
  const [delegate, setDelegate] = useState(initialData?.delegate || '');
  const [sensitive, setSensitive] = useState(initialData?.sensitive || false);
  const isEditing = !!initialData;
  const hasAdvancedData = Boolean(initialData?.details || initialData?.due_date || initialData?.delegate);
  const [showAdvanced, setShowAdvanced] = useState(isEditing || hasAdvancedData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle Escape key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel) {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        details: details.trim() || undefined,
        due_date: dueDate || undefined,
        delegate: delegate.trim() || undefined,
        sensitive: isEditing ? sensitive : undefined,
      });
      // Only reset form when adding new (not editing)
      if (!initialData) {
        setTitle('');
        setDetails('');
        setDueDate('');
        setDelegate('');
        setShowAdvanced(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="space-y-2">
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                     placeholder-zinc-400 dark:placeholder-zinc-500"
          autoFocus
        />
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400
                         dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!title.trim() || isSubmitting}
            className="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600
                       dark:bg-blue-400 dark:text-zinc-900 dark:hover:bg-blue-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          disabled={readOnly}
          className={`w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg
                     bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                     placeholder-zinc-400 dark:placeholder-zinc-500
                     ${readOnly ? 'cursor-default' : ''}`}
          autoFocus={!readOnly}
        />
      </div>

      {showAdvanced ? (
        <>
          <div>
            <textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              placeholder="Details (optional)"
              rows={2}
              disabled={readOnly}
              className={`w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                         bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                         focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                         placeholder-zinc-400 dark:placeholder-zinc-500
                         ${readOnly ? 'cursor-default' : ''}`}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              {(dueDate || !readOnly) && (
                <>
                  <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                    Due date
                  </label>
                  {readOnly ? (
                    <p className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                      {dueDate ? new Date(dueDate + 'T12:00:00').toLocaleDateString() : '—'}
                    </p>
                  ) : dueDate ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                                   bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                                   focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setDueDate('')}
                        className="p-2 text-zinc-400 hover:text-red-500"
                        aria-label="Clear date"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date().toISOString().split('T')[0];
                        setDueDate(today);
                      }}
                      className="w-full px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400 border border-dashed
                                 border-zinc-300 dark:border-zinc-700 rounded-lg hover:border-zinc-400
                                 dark:hover:border-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                    >
                      + Add due date
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              {(delegate || !readOnly) && (
                <>
                  <label className="block text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                    Delegate to
                  </label>
                  {readOnly ? (
                    <p className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                      {delegate
                        ? delegate.split(/[,\s]+/).filter(Boolean).map(d => `@${d.replace(/^@/, '')}`).join(', ')
                        : '—'}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={delegate}
                      onChange={e => setDelegate(e.target.value)}
                      placeholder="@user1, @user2"
                      className="w-full px-3 py-2 text-sm border border-zinc-300 dark:border-zinc-700 rounded-lg
                                 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                                 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                                 placeholder-zinc-400 dark:placeholder-zinc-500"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        !readOnly && (
          <button
            type="button"
            onClick={() => setShowAdvanced(true)}
            className="text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            + Add details, due date, or delegate
          </button>
        )
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {decisionId && (
            <span className="text-xs">
              <span className="text-zinc-500 dark:text-zinc-400">Linked to: </span>
              <a
                href={`/decisions/${decisionId}`}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View decision →
              </a>
            </span>
          )}
          {isEditing && !readOnly && (
            <button
              type="button"
              onClick={() => setSensitive(!sensitive)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                sensitive
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              {sensitive ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
              {sensitive ? 'Sensitive' : 'Mark Sensitive'}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400
                         dark:hover:text-zinc-200"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
          )}
          {!readOnly && (
            <button
              type="submit"
              disabled={!title.trim() || isSubmitting}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:text-zinc-900 dark:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (isEditing ? 'Updating...' : 'Adding...') : isEditing ? 'Update' : 'Add todo'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
